import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@marine/utils/getErrorMessage';
import { tenantApi } from '@/modules/tenant/services/tenantApi';
import { notificationKeys } from '../useNotifications';

/**
 * #733 — the assign endpoint as a Core-side STRING CONSTANT.
 *
 * Core is always loaded; CRM is an optional per-tenant module. Importing CRM's
 * leadApi here would give always-loaded code a build-time edge into a module a
 * tenant can disable. A constant has no such edge, and the URL is not stored in
 * the database either — the server authors only the IDs.
 */
// VERIFIED against `php artisan route:list --path=assign`: the real URI is
// `api/leads/{id}/assign`. There is NO `/crm` segment — the CRM module's routes
// are registered without a module prefix, exactly like leadApi's other calls
// (`/leads/${id}`, `/leads/${id}/move-stage`). tenantApi prepends `/api`.
const assignLeadUrl = (leadId: string) => `/leads/${leadId}/assign`;

/**
 * #733 fix-round-1 — human-legible message for a failed inline assign.
 *
 * Exported so `NotificationActions` can render the identical text inline
 * (`role="status"`) alongside the toast this hook raises on `onError` — a
 * silent failure is worse for a screen-reader user than a sighted one, so the
 * message needs to exist in the DOM, not just in a transient toast.
 *
 * The 403 case is special-cased: Task 5 gates `POST /leads/{id}/assign` behind
 * the `leads.assign` permission, and Laravel's raw 403 body ("This action is
 * unauthorized.") doesn't say what the dispatcher was trying to do.
 */
export function assignLeadErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.status === 403) {
    return 'You do not have permission to assign inquiries';
  }
  return getErrorMessage(error, 'Failed to assign inquiry');
}

export function useAssignLeadFromNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, employeeId }: { leadId: string; employeeId: string }) =>
      tenantApi.postOne<{ id: string }>(assignLeadUrl(leadId), { assigned_to: employeeId }),
    onSuccess: () => {
      // Both surfaces move: the bell (this notification is now actioned) and
      // every lead list/kanban/ownership-count query from #701.
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      // Literal root, NOT an import of leadKeys from modules/crm — see the
      // module-boundary note above. Verified equal to leadKeys.all
      // (useLeads.ts:8 `all: ['leads'] as const`), which covers lists, kanban
      // and #701's ownership counts because they all extend that root.
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    // #733 fix-round-1 — every error was previously swallowed silently: no
    // onError here, and the component never read assign.isError either, so a
    // 403 (no leads.assign permission), a 422, or a network failure all
    // looked identical to success from the button re-enabling with nothing
    // else happening. Matches the sibling hook's convention
    // (useEmployees.ts's useCreateEmployee/useUpdateEmployee/useDeleteEmployee
    // all call toast.error(getErrorMessage(...)) in onError).
    onError: (error) => {
      toast.error(assignLeadErrorMessage(error));
    },
  });
}
